import { observer } from 'mobx-react-lite'
import { ActionToggle } from '@/components/ActionToggle'
import { Button } from '@/components/Button'
import { FormGroupContainer } from '@/components/FormContainer'
import { NumericInputContainer } from '@/components/inputs/NumericInputContainer'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import type { LegendEditorViewModel } from '@/features/legends/LegendEditor/LegendEditorViewModel'
import { DurationInput } from '@/features/legends/options/components/DurationInput'
import { RoutingDataSourcesContainer } from '@/features/legends/data-sources/components/RoutingDataSourcesContainer'
import {
  MultiOptionSelect,
  SingleOptionSelect,
} from '@/features/legends/components/OptionSelect'
import { OptionsCard } from '@/features/legends/options/components/OptionsCard'
import { VersionNotesContainer } from '@/features/legends/options/components/VersionNotes/VersionNotesContainer'
import type { DeadlineConfigEditorViewModel } from '@/features/legends/options/DeadlineConfigEditorViewModel'
import type { LegendOptionsViewModel } from '@/features/legends/options/LegendOptionsViewModel'
import type { MessageConfigEditorViewModel } from '@/features/legends/options/MessageConfigEditorViewModel'
import type { PendingConfigEditorViewModel } from '@/features/legends/options/PendingConfigEditorViewModel'
import type { PrepAndReviewConfigEditorViewModel } from '@/features/legends/options/PrepAndReviewConfigEditorViewModel'
import type { PurgeConfigEditorViewModel } from '@/features/legends/options/PurgeConfigEditorViewModel'
import type { RatingConfigEditorViewModel } from '@/features/legends/options/RatingConfigEditorViewModel'
import type { StalenessConfigEditorViewModel } from '@/features/legends/options/StalenessConfigEditorViewModel'
import type { TimeConfigEditorViewModel } from '@/features/legends/options/TimeConfigEditorViewModel'
import type { TxfConfigEditorViewModel } from '@/features/legends/options/TxfConfigEditorViewModel'
import { FormulaFieldContainer } from '@/features/legends/ryno/components/FormulaFieldContainer'

export interface GeneralOptionsContainerProps {
  vm: LegendEditorViewModel
}

const testId = 'legend-general-options'

/**
 * The legend's general options, as a column of settings cards.
 */
export const GeneralOptionsContainer = observer(
  function GeneralOptionsContainer({ vm }: GeneralOptionsContainerProps) {
    const optionsVm = vm.optionsVm
    return (
      <>
        <VersionNotesContainer vm={vm} />
        <InformationCardContainer vm={optionsVm} />
        <TimeZoneCardContainer vm={optionsVm.timeConfig} />
        <DeadlineCardContainer vm={optionsVm.deadlineConfig} />
        <PendingCardContainer vm={optionsVm.pendingConfig} />
        <OptionsCard
          title="Title"
          description="Configure how the request title is computed."
          testId={`${testId}-title`}
        >
          <FormulaFieldContainer
            label="Title formula"
            help="Ryno formula to compute the title."
            vm={optionsVm.titleConfig.formula}
            error={optionsVm.titleConfig.validation.getError('formula')}
            isDisabled={optionsVm.titleConfig.isEditingDisabled}
            testId={`${testId}-title-formula`}
          />
        </OptionsCard>
        <MessagesCardContainer vm={optionsVm.messageConfig} />
        <NotificationsCardContainer vm={optionsVm} />
        <StalenessCardContainer vm={optionsVm.staleness} />
        <PurgeCardContainer vm={optionsVm.purge} />
        <RatingCardContainer vm={optionsVm.ratingConfig} />
        <OptionsCard
          title="Billing"
          description="Settings for billing."
          testId={`${testId}-billing`}
        >
          <ActionToggle
            label="Skip billing"
            description="If this is checked, no charges are made."
            value={optionsVm.skipBilling}
            onChange={optionsVm.setSkipBilling}
            isDisabled={optionsVm.isEditingDisabled}
            testId={`${testId}-skip-billing`}
          />
        </OptionsCard>
        {vm.isTaxLegend && (
          <PrepAndReviewCardContainer vm={optionsVm.prepAndReviewConfig} />
        )}
        <OptionsCard
          title="Routing data sources"
          description="Bring in additional data that can be used for routing purposes, specifically related to the candidate being matched."
          testId={`${testId}-data-sources`}
        >
          <RoutingDataSourcesContainer
            vm={optionsVm.dataSources.routingContextSources}
          />
        </OptionsCard>
        {optionsVm.isTxfEnabled && (
          <TxfCardContainer vm={optionsVm.txfConfig} />
        )}
        {optionsVm.isTaxPlanEnabled && (
          <OptionsCard
            title="Tax Plan"
            description="Whether this job is eligible for Tax Plan integration."
            testId={`${testId}-tax-plan`}
          >
            <FormulaFieldContainer
              label="Eligibility formula"
              help="Ryno formula to compute Tax Plan eligibility."
              vm={optionsVm.taxPlanConfig.formula}
              error={optionsVm.taxPlanConfig.validation.getError('formula')}
              isDisabled={optionsVm.taxPlanConfig.isEditingDisabled}
              testId={`${testId}-tax-plan-formula`}
            />
          </OptionsCard>
        )}
        {vm.isTaxLegend && (
          <OptionsCard
            title="Personal organizer"
            description="Whether this job uses the personal tax organizer."
            testId={`${testId}-personal-organizer`}
          >
            <FormulaFieldContainer
              label="Enabled formula"
              help="Ryno formula to compute whether the job uses the personal tax organizer. If empty, the organizer is not used."
              vm={optionsVm.personalOrganizerConfig.formula}
              error={optionsVm.personalOrganizerConfig.validation.getError(
                'formula',
              )}
              isDisabled={optionsVm.personalOrganizerConfig.isEditingDisabled}
              testId={`${testId}-personal-organizer-formula`}
            />
          </OptionsCard>
        )}
        <MiscellaneousCardContainer vm={optionsVm} />
      </>
    )
  },
)

interface InformationCardContainerProps {
  vm: LegendOptionsViewModel
}

const InformationCardContainer = observer(function InformationCardContainer({
  vm,
}: InformationCardContainerProps) {
  return (
    <OptionsCard
      title={vm.name || 'Information'}
      description={vm.description || 'Basic legend information.'}
      imageUrl={vm.imageUrl || undefined}
      testId={`${testId}-information`}
    >
      <TextInputContainer
        model={vm}
        field="name"
        label="Legend name"
        help="The name of the legend, as seen on the request type selection screen."
        error={vm.validation.getError('name')}
        isDisabled={vm.isEditingDisabled}
        data-test={`${testId}-name`}
      />
      <TextInputContainer
        model={vm}
        field="description"
        label="Description"
        help="Visible on the request type selection screen. Keep it short."
        error={vm.validation.getError('description')}
        isDisabled={vm.isEditingDisabled}
        data-test={`${testId}-description`}
      />
      <TextInputContainer
        model={vm}
        field="imageUrl"
        type="url"
        label="Icon URL"
        help="The URL of the icon you would like to use. (Optional)"
        placeholder="https://"
        error={vm.validation.getError('imageUrl')}
        isDisabled={vm.isEditingDisabled}
        data-test={`${testId}-image-url`}
      />
      <FormGroupContainer
        label="Target roles"
        help="This legend is only visible to the selected roles. If nothing is selected, it is visible to everyone."
        error={vm.validation.getError('targetRoles')}
      >
        <MultiOptionSelect
          value={vm.targetRoles}
          options={vm.availableRoles}
          onChange={vm.setTargetRoles}
          placeholder="Select…"
          isDisabled={vm.isEditingDisabled}
          isInvalid={!!vm.validation.getError('targetRoles')}
          testId={`${testId}-target-roles`}
        />
      </FormGroupContainer>
      <SelectContainer
        model={vm}
        field="serviceType"
        items={vm.serviceTypes}
        label="Service type"
        help="The type of job that will be created. If nothing is selected, the legend is legacy."
        placeholder="Select service type"
        error={vm.validation.getError('serviceType')}
        isDisabled={vm.isEditingDisabled || !vm.canChangeServiceType}
        data-test={`${testId}-service-type`}
      />
    </OptionsCard>
  )
})

interface TimeZoneCardContainerProps {
  vm: TimeConfigEditorViewModel
}

const TimeZoneCardContainer = observer(function TimeZoneCardContainer({
  vm,
}: TimeZoneCardContainerProps) {
  return (
    <OptionsCard
      title="Time zone"
      description="Configure the legend's time zone."
      testId={`${testId}-time-zone`}
    >
      <SelectContainer
        model={vm}
        field="timeZone"
        items={vm.availableTimeZones}
        label="Time zone"
        help="The selected time zone is used when formatting dates using the Ryno 'Text' function."
        error={vm.validation.getError('timeZone')}
        isDisabled={vm.isEditingDisabled}
        data-test={`${testId}-time-zone`}
      />
    </OptionsCard>
  )
})

interface DeadlineCardContainerProps {
  vm: DeadlineConfigEditorViewModel
}

const DeadlineCardContainer = observer(function DeadlineCardContainer({
  vm,
}: DeadlineCardContainerProps) {
  return (
    <OptionsCard
      title="Deadline"
      description="Configure how the request deadline is computed."
      testId={`${testId}-deadline`}
    >
      <FormulaFieldContainer
        label="Deadline formula"
        vm={vm.expr}
        error={vm.validation.getError('expr')}
        isDisabled={vm.isEditingDisabled}
        testId={`${testId}-deadline-formula`}
        help={
          <>
            Ryno formula to compute the deadline.
            {!vm.isEditingDisabled && (
              <>
                {' '}
                For example,{' '}
                <Button
                  label="7 days from request submission"
                  variant="text"
                  size="baseline"
                  onPress={vm.applyTemplate}
                  testId={`${testId}-deadline-template`}
                />
                .
              </>
            )}{' '}
            If not specified, no deadline is used.
          </>
        }
      />
      {vm.expr.value && (
        <>
          <ActionToggle
            label="Reminders"
            description="Reminds users that the deadline is coming up."
            value={vm.reminder.enabled}
            onChange={vm.reminder.setEnabled}
            isDisabled={vm.isEditingDisabled}
            testId={`${testId}-reminders`}
          />
          {vm.reminder.enabled && (
            <NumericInputContainer
              model={vm.reminder}
              field="daysBefore"
              label="Days before reminder"
              help="The number of days before the deadline when users will be reminded. Should be between 1 and 21."
              error={vm.reminder.validation.getError('daysBefore')}
              isDisabled={vm.isEditingDisabled}
              testId={`${testId}-reminder-days`}
            />
          )}
        </>
      )}
    </OptionsCard>
  )
})

interface PendingCardContainerProps {
  vm: PendingConfigEditorViewModel
}

const PendingCardContainer = observer(function PendingCardContainer({
  vm,
}: PendingCardContainerProps) {
  return (
    <OptionsCard
      title="Pending"
      description="Configure whether requests start pending and when they are allowed to advance."
      testId={`${testId}-pending`}
    >
      <FormulaFieldContainer
        label="Eligibility formula"
        help="Ryno formula to compute pending eligibility."
        vm={vm.enabledExpr}
        error={vm.validation.getError('enabledExpr')}
        isDisabled={vm.isEditingDisabled}
        testId={`${testId}-pending-formula`}
      />
      <FormulaFieldContainer
        label="Advance date"
        vm={vm.advanceDateExpr}
        error={vm.validation.getError('advanceDateExpr')}
        isDisabled={vm.isEditingDisabled}
        testId={`${testId}-advance-date-formula`}
        help={
          <>
            Ryno formula to compute when the pending request is allowed to
            advance.
            {!vm.isEditingDisabled && (
              <>
                {' '}
                For example, at{' '}
                <Button
                  label="a fixed date"
                  variant="text"
                  size="baseline"
                  onPress={vm.applyFixedDateTemplate}
                  testId={`${testId}-advance-date-fixed-template`}
                />{' '}
                or{' '}
                <Button
                  label="3 months from request submission"
                  variant="text"
                  size="baseline"
                  onPress={vm.applyRelativeDateTemplate}
                  testId={`${testId}-advance-date-relative-template`}
                />
                .
              </>
            )}{' '}
            If not specified, no advance date is used.
          </>
        }
      />
    </OptionsCard>
  )
})

interface MessagesCardContainerProps {
  vm: MessageConfigEditorViewModel
}

const MessagesCardContainer = observer(function MessagesCardContainer({
  vm,
}: MessagesCardContainerProps) {
  return (
    <OptionsCard
      title="Messages"
      description="Settings related to sent messages."
      testId={`${testId}-messages`}
    >
      <ActionToggle
        label="Enable welcome message"
        description="Will send a message to a user and Pro once the job is picked up."
        value={vm.welcomeMessageEnabled}
        onChange={vm.setWelcomeMessageEnabled}
        isDisabled={vm.isEditingDisabled}
        testId={`${testId}-welcome-message`}
      />
      {vm.welcomeMessageEnabled && (
        <TextAreaContainer
          model={vm}
          field="welcomeMessageText"
          label="Text"
          help="Provide a message that will show in the chat once the job is picked up."
          error={vm.validation.getError('welcomeMessageText')}
          isDisabled={vm.isEditingDisabled}
          data-test={`${testId}-welcome-message-text`}
        />
      )}
    </OptionsCard>
  )
})

interface NotificationsCardContainerProps {
  vm: LegendOptionsViewModel
}

const NotificationsCardContainer = observer(
  function NotificationsCardContainer({ vm }: NotificationsCardContainerProps) {
    return (
      <OptionsCard
        title="Notifications"
        description="Settings related to request notifications."
        testId={`${testId}-notifications`}
      >
        <ActionToggle
          label="Send notifications on submit"
          description="When requests are submitted, sends out a notification to eligible workers that a new request is available."
          value={vm.sendNotificationsOnSubmit}
          onChange={vm.setSendNotificationsOnSubmit}
          isDisabled={vm.isEditingDisabled}
          testId={`${testId}-notify-on-submit`}
        />
        {vm.sendNotificationsOnSubmit && (
          <FormGroupContainer
            label="Delay notification"
            help="Useful to avoid spamming workers for requests that get picked up fast."
            error={vm.validation.getError('delayNotification')}
          >
            <DurationInput
              value={vm.delayNotification}
              onChange={vm.setDelayNotification}
              isDisabled={vm.isEditingDisabled}
              isInvalid={!!vm.validation.getError('delayNotification')}
              testId={`${testId}-notification-delay`}
            />
          </FormGroupContainer>
        )}
      </OptionsCard>
    )
  },
)

interface StalenessCardContainerProps {
  vm: StalenessConfigEditorViewModel
}

const StalenessCardContainer = observer(function StalenessCardContainer({
  vm,
}: StalenessCardContainerProps) {
  return (
    <OptionsCard
      title="Staleness"
      description="Configure when requests go stale. When this happens, notifications will be dispatched to collaborators."
      testId={`${testId}-staleness`}
    >
      <ActionToggle
        label="Enable staleness"
        description="Requests go stale when there has been no activity on them for the specified amount of time."
        value={vm.enabled}
        onChange={vm.setEnabled}
        isDisabled={vm.isEditingDisabled}
        testId={`${testId}-staleness-enabled`}
      />
      {vm.enabled && (
        <>
          <FormGroupContainer
            label="Requests go stale after"
            help="When elapsed, the request is marked as stale, and notifications will be dispatched to collaborators."
            error={vm.validation.getError('staleAfter')}
          >
            <DurationInput
              value={vm.staleAfter}
              onChange={vm.setStaleAfter}
              isDisabled={vm.isEditingDisabled}
              isInvalid={!!vm.validation.getError('staleAfter')}
              testId={`${testId}-stale-after`}
            />
          </FormGroupContainer>
          <ActionToggle
            label="Recurring"
            description="Notify collaborators more than once, until the request becomes active again."
            value={vm.recurring}
            onChange={vm.setRecurring}
            isDisabled={vm.isEditingDisabled}
            testId={`${testId}-staleness-recurring`}
          />
        </>
      )}
    </OptionsCard>
  )
})

interface PurgeCardContainerProps {
  vm: PurgeConfigEditorViewModel
}

const PurgeCardContainer = observer(function PurgeCardContainer({
  vm,
}: PurgeCardContainerProps) {
  return (
    <OptionsCard
      title="Purge"
      description="Automatically purge requests with no activity after a specified amount of time."
      testId={`${testId}-purge`}
    >
      <ActionToggle
        label="Enable purging"
        description="Requests with no activity after the specified amount of time become eligible for purging."
        value={vm.enabled}
        onChange={vm.setEnabled}
        isDisabled={vm.isEditingDisabled}
        testId={`${testId}-purge-enabled`}
      />
      {vm.enabled && (
        <FormGroupContainer
          label="Purge requests after"
          help="When elapsed, the request is eligible for purging, and will eventually have the specified data removed."
          error={vm.validation.getError('purgeAfter')}
        >
          <DurationInput
            value={vm.purgeAfter}
            onChange={vm.setPurgeAfter}
            isDisabled={vm.isEditingDisabled}
            isInvalid={!!vm.validation.getError('purgeAfter')}
            testId={`${testId}-purge-after`}
          />
        </FormGroupContainer>
      )}
    </OptionsCard>
  )
})

interface RatingCardContainerProps {
  vm: RatingConfigEditorViewModel
}

const RatingCardContainer = observer(function RatingCardContainer({
  vm,
}: RatingCardContainerProps) {
  return (
    <OptionsCard
      title="Rating"
      description="Configuration for the rating system."
      testId={`${testId}-rating`}
    >
      <ActionToggle
        label="Enable Pro ratings"
        description="If enabled, the client can rate the Pro."
        value={vm.proRatingEnabled}
        onChange={vm.setProRatingEnabled}
        isDisabled={vm.isEditingDisabled}
        testId={`${testId}-pro-rating`}
      />
      <ActionToggle
        label="Enable platform ratings"
        description="If enabled, the client can rate the platform experience."
        value={vm.platformRatingEnabled}
        onChange={vm.setPlatformRatingEnabled}
        isDisabled={vm.isEditingDisabled}
        testId={`${testId}-platform-rating`}
      />
      {vm.hasRatings && (
        <SelectContainer
          model={vm}
          field="customerRatingMethod"
          items={vm.ratingMethods}
          label="Customer rating method"
          help="Select the preferred rating method."
          error={vm.validation.getError('customerRatingMethod')}
          isDisabled={vm.isEditingDisabled}
          data-test={`${testId}-rating-method`}
        />
      )}
    </OptionsCard>
  )
})

interface PrepAndReviewCardContainerProps {
  vm: PrepAndReviewConfigEditorViewModel
}

const PrepAndReviewCardContainer = observer(
  function PrepAndReviewCardContainer({ vm }: PrepAndReviewCardContainerProps) {
    return (
      <OptionsCard
        title="Prep and Review"
        description="Create a flow for Prep and Review jobs."
        testId={`${testId}-prep-and-review`}
      >
        <ActionToggle
          label="Enable Prep and Review flow"
          description="Splits the job into a Prep job and a Review job."
          value={vm.enabled}
          onChange={vm.setEnabled}
          isDisabled={vm.isEditingDisabled}
          testId={`${testId}-prep-and-review-enabled`}
        />
        {vm.enabled && (
          <>
            <ActionToggle
              label="Send to previous Pro"
              description="If enabled, the job will be sent to the previous pro."
              value={vm.previousPro}
              onChange={vm.setPreviousPro}
              isDisabled={vm.isEditingDisabled}
              testId={`${testId}-prep-and-review-previous-pro`}
            />
            <FormGroupContainer
              label="The service item that indicates this is a Prep job"
              help="The job would utilize prep pricing when this service item is present on the job."
              error={vm.validation.getError('prepServiceItemId')}
            >
              <SingleOptionSelect
                value={vm.prepServiceItemId}
                options={vm.availableInventoryItems}
                onChange={vm.setPrepServiceItemId}
                placeholder="Select a service item…"
                isDisabled={vm.isEditingDisabled}
                isInvalid={!!vm.validation.getError('prepServiceItemId')}
                testId={`${testId}-prep-service-item`}
              />
            </FormGroupContainer>
            <FormGroupContainer
              label="The service item that indicates this is a Review job"
              help="The job would utilize review pricing when this service item is present on the job."
              error={vm.validation.getError('reviewServiceItemId')}
            >
              <SingleOptionSelect
                value={vm.reviewServiceItemId}
                options={vm.availableInventoryItems}
                onChange={vm.setReviewServiceItemId}
                placeholder="Select a service item…"
                isDisabled={vm.isEditingDisabled}
                isInvalid={!!vm.validation.getError('reviewServiceItemId')}
                testId={`${testId}-review-service-item`}
              />
            </FormGroupContainer>
            <FormGroupContainer
              label="The service item that indicates this job needs Prep and Review"
              help="The first job would utilize prep pricing and the second job would utilize review pricing."
              error={vm.validation.getError('prepAndReviewServiceItemId')}
            >
              <SingleOptionSelect
                value={vm.prepAndReviewServiceItemId}
                options={vm.availableInventoryItems}
                onChange={vm.setPrepAndReviewServiceItemId}
                placeholder="Select a service item…"
                isDisabled={vm.isEditingDisabled}
                isInvalid={
                  !!vm.validation.getError('prepAndReviewServiceItemId')
                }
                testId={`${testId}-prep-and-review-service-item`}
              />
            </FormGroupContainer>
          </>
        )}
      </OptionsCard>
    )
  },
)

interface TxfCardContainerProps {
  vm: TxfConfigEditorViewModel
}

const TxfCardContainer = observer(function TxfCardContainer({
  vm,
}: TxfCardContainerProps) {
  return (
    <OptionsCard
      title="TXF"
      description="Whether this job is eligible for TXF integration."
      testId={`${testId}-txf`}
    >
      <FormulaFieldContainer
        label="Eligibility formula"
        help="Ryno formula to compute eligibility."
        vm={vm.integrationEnabledExpr}
        error={vm.validation.getError('integrationEnabledExpr')}
        isDisabled={vm.isEditingDisabled}
        testId={`${testId}-txf-formula`}
      />
      <ActionToggle
        label="Show questionnaire"
        description="If enabled, the questionnaire will be shown to the client."
        value={vm.showQuestionnaire}
        onChange={vm.setShowQuestionnaire}
        isDisabled={vm.isEditingDisabled}
        testId={`${testId}-txf-questionnaire`}
      />
    </OptionsCard>
  )
})

interface MiscellaneousCardContainerProps {
  vm: LegendOptionsViewModel
}

const MiscellaneousCardContainer = observer(
  function MiscellaneousCardContainer({ vm }: MiscellaneousCardContainerProps) {
    return (
      <OptionsCard
        title="Miscellaneous"
        description="Stuff that does not really fit in anywhere else."
        testId={`${testId}-miscellaneous`}
      >
        <NumericInputContainer
          model={vm}
          field="viewOrder"
          label="View order"
          help="Legends are sorted ascending by this number."
          allowNegative
          error={vm.validation.getError('viewOrder')}
          isDisabled={vm.isEditingDisabled}
          testId={`${testId}-view-order`}
        />
        <ActionToggle
          label="Allow Provider selection"
          description="If enabled, the client can assign a previous Provider directly to their job during onboarding."
          value={vm.allowProSelection}
          onChange={vm.setAllowProSelection}
          isDisabled={vm.isEditingDisabled}
          testId={`${testId}-allow-pro-selection`}
        />
        <ActionToggle
          label="Show Provider availability"
          description="If enabled, the client will see how many Providers will match with their job before submitting."
          value={vm.showAvailabilityCount}
          onChange={vm.setShowAvailabilityCount}
          isDisabled={vm.isEditingDisabled}
          testId={`${testId}-show-availability`}
        />
        <ActionToggle
          label="Always enable call"
          description="Shows the call card in Work Portal even if the client did not purchase a Consultation."
          value={vm.alwaysEnableVoiceCall}
          onChange={vm.setAlwaysEnableVoiceCall}
          isDisabled={vm.isEditingDisabled}
          testId={`${testId}-always-enable-call`}
        />
      </OptionsCard>
    )
  },
)
